import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Cpu, TrendingUp, Scale, Rocket, Route, Sparkles, Check, Loader2, X } from "lucide-react";

const FALLBACK = ["Initializing the cognitive core", "Processing", "Please wait"];

const ENGINE_META = {
  Rexxpons: { icon: Globe, label: "Rexxpons", role: "Web Browser", color: "text-primary", ring: "border-primary/40 bg-primary/10" },
  Rexxengine: { icon: Cpu, label: "Rexxengine", role: "Reasoning", color: "text-violet-300", ring: "border-violet-500/40 bg-violet-500/10" },
  RatingOnline: { icon: TrendingUp, label: "RatingOnline", role: "Financial", color: "text-amber-300", ring: "border-amber-500/40 bg-amber-500/10" },
  RexxLegal: { icon: Scale, label: "RexxLegal", role: "Legal", color: "text-emerald-300", ring: "border-emerald-500/40 bg-emerald-500/10" },
  GrowthCompass: { icon: Rocket, label: "Growth-Compass", role: "Strategic Growth", color: "text-fuchsia-300", ring: "border-fuchsia-500/40 bg-fuchsia-500/10" },
  Decision: { icon: Route, label: "Decision Layer", role: "Synthesis", color: "text-rose-300", ring: "border-rose-500/40 bg-rose-500/10" },
  Fusion: { icon: Sparkles, label: "Cognitive Fusion", role: "Fusion", color: "text-primary", ring: "border-primary/40 bg-primary/10" },
};
const ENGINE_ORDER = ["Rexxpons", "Rexxengine", "RatingOnline", "RexxLegal", "GrowthCompass", "Decision", "Fusion"];

const StatusIcon = ({ status, color }) => {
  if (status === "done") return <Check strokeWidth={2.4} className="w-4 h-4 text-emerald-400" />;
  if (status === "error") return <X strokeWidth={2.4} className="w-4 h-4 text-rose-400" />;
  return <Loader2 strokeWidth={2} className={`w-4 h-4 ${color} animate-spin`} />;
};

const EngineTimeline = ({ engines }) => {
  const items = ENGINE_ORDER.filter((k) => engines[k]);
  return (
    <div data-testid="engine-timeline" className="mt-12 w-full max-w-md mx-auto space-y-2.5">
      <p className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">Systems being consulted</p>
      <AnimatePresence>
        {items.map((k, i) => {
          const m = ENGINE_META[k];
          const status = engines[k];
          const Icon = m.icon;
          return (
            <motion.div
              key={k}
              data-testid={`timeline-engine-${k.toLowerCase()}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-left ${status === "done" ? "border-border bg-secondary/30" : m.ring} transition-colors`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Icon strokeWidth={1.8} className={`w-4 h-4 shrink-0 ${status === "done" ? "text-muted-foreground" : m.color}`} />
                <span className="min-w-0">
                  <span className="font-sans text-sm text-foreground">{m.label}</span>
                  <span className="font-sans text-[10px] tracking-[0.14em] uppercase text-muted-foreground ml-2">{m.role}</span>
                </span>
              </div>
              <StatusIcon status={status} color={m.color} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export const Orchestration = ({ query, phase, kind = "search", engines }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (phase) return undefined;
    const id = setInterval(() => setIdx((i) => (i < FALLBACK.length - 1 ? i + 1 : i)), 2200);
    return () => clearInterval(id);
  }, [phase]);

  const label = phase || FALLBACK[idx];
  const eyebrow = kind === "interpret" ? "Interpreting" : "Synthesizing";
  const hasEngines = engines && Object.keys(engines).length > 0;

  return (
    <div className="relative z-10 min-h-screen w-full flex flex-col items-center justify-center px-6 py-28">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-3xl text-center"
      >
        <p className="text-[11px] tracking-[0.3em] uppercase text-primary/80 mb-6">{eyebrow}</p>
        <h2 className="font-heading font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug text-foreground mb-14 px-4 break-words">
          {kind === "interpret" ? query.replace(/^https?:\/\//, "") : `“${query}”`}
        </h2>

        <div className="h-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45 }}
              data-testid="orchestration-phase"
              className="text-sm sm:text-base text-muted-foreground flex items-center gap-2.5"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              {label}…
            </motion.p>
          </AnimatePresence>
        </div>

        {hasEngines ? (
          <EngineTimeline engines={engines} />
        ) : (
          <div className="mt-12 w-full max-w-md mx-auto h-px bg-border overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: "5%" }}
              animate={{ width: "94%" }}
              transition={{ duration: 22, ease: "easeInOut" }}
            />
          </div>
        )}
      </motion.div>
    </div>
  );
};
